import { makeAutoObservable } from 'mobx';
import { ToastOptions, toast } from 'react-toastify';
import { makePersistable } from 'mobx-persist-store';
import React from 'react';
import { store } from './store';
import { ApiResponse } from '../models/apiResponse';
import { ReactComponent as InfoIcon } from '../assets/info-blue.svg';
import { ReactComponent as SuccessIcon } from '../assets/success.svg';
import { ReactComponent as ErrorIcon } from '../assets/error.svg';
import { ReactComponent as AlertIcon } from '../assets/alert-triangle-yellow.svg';
import ToastMessage, { ToastContent } from '../common/toasts/toast-message';
import { normalizeToToastContent } from '../common/toasts/utils';
import AnalyticsManager from '../services/analytics/analytics-manager';
import { testIds } from '../../test-ids';
import { isMeest } from '../utils/client/client.utils';
import { client } from '../config';

interface ResetAppStateOptions {
  preserveParcelCreationState?: boolean;
}

export default class CommonStore {
  serverError: ApiResponse | null = null;

  accessToken: string | null = null;

  refreshToken: string | null = null;

  guestAccessToken: string | null = null;

  guestRefreshToken: string | null = null;

  isGuestParcelCreation: boolean | null = null;

  appLoaded: boolean = false;

  isOffline: boolean = false;

  isLaunchNotificationShown: boolean = false;

  acceptedAllCookies: string | null = null;

  toastInfoOptions: ToastOptions = { className: 'toastInfoCustom' };

  toastInfoWithIconOptions: ToastOptions = {
    className: 'toastInfoCustom',
    icon: <InfoIcon />,
  };

  toastSuccessOptions: ToastOptions = {
    className: 'toastSuccessCustom',
    icon: <SuccessIcon />,
  };

  toastErrorOptions: ToastOptions = {
    className: 'toastErrorCustom',
    icon: <ErrorIcon />,
  };

  toastErrorMissingPhoneNumberOptions: ToastOptions = {
    className: 'toastErrorCustom ErrorMissingPhoneNumber',
    icon: <ErrorIcon />,
  };

  toastAlertOptions: ToastOptions = {
    className: 'toastWarningCustom',
    icon: <AlertIcon />,
  };

  analyticsManager: AnalyticsManager = new AnalyticsManager();

  isAnalyticsInitialized: boolean = false;

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'CommonStore',
      properties: ['isOffline', 'acceptedAllCookies', 'isGuestParcelCreation'],
      storage: window.localStorage,
    });
    makePersistable(this, {
      name: 'CommonStoreSession',
      properties: [
        'isLaunchNotificationShown',
        'guestAccessToken',
        'guestRefreshToken',
      ],
      storage: window.sessionStorage,
    });
  }

  get landingPageUrl() {
    // TODO: replace with env var!
    return isMeest(client)
      ? `https://${store.localizationsStore.selectedCountry?.alias.toLocaleLowerCase()}.meest.com/`
      : `https://polonez.express${store.localizationsStore.localeParam}`;
  }

  setServerError = (error: ApiResponse) => {
    this.serverError = error;
  };

  setAccessToken = (token: string | null) => {
    this.accessToken = token;
  };

  setRefreshToken = (token: string | null) => {
    this.refreshToken = token;
  };

  setGuestAccessToken = (token: string | null) => {
    this.guestAccessToken = token;
  };

  setGuestRefreshToken = (token: string | null) => {
    this.guestRefreshToken = token;
  };

  setAppLoaded = async (loaded: boolean) => {
    this.appLoaded = loaded;
  };

  setAcceptedAllCookies = (isAcceptedAllCookies: string | null) => {
    this.acceptedAllCookies = isAcceptedAllCookies;
  };

  setToastErrorOptions = (newOptions: ToastOptions) => {
    this.toastSuccessOptions = newOptions;
  };

  toastInfo = (
    message: string | ToastContent | React.ReactNode,
    showIcon?: boolean
  ) => {
    const contentToRender = React.isValidElement(message) ? (
      message
    ) : (
      <ToastMessage
        content={normalizeToToastContent(message)}
        testId={testIds.common.toast.info}
      />
    );

    toast(
      contentToRender,
      showIcon ? this.toastInfoWithIconOptions : this.toastInfoOptions
    );
  };

  toastSuccess = (message: string | ToastContent | React.ReactNode) => {
    const contentToRender = React.isValidElement(message) ? (
      message
    ) : (
      <ToastMessage
        content={normalizeToToastContent(message)}
        testId={testIds.common.toast.success}
      />
    );

    toast.success(contentToRender, this.toastSuccessOptions);
  };

  toastError = (message: string | ToastContent | React.ReactNode) => {
    const contentToRender = React.isValidElement(message) ? (
      message
    ) : (
      <ToastMessage
        content={normalizeToToastContent(message)}
        testId={testIds.common.toast.error}
      />
    );

    toast.error(contentToRender, this.toastErrorOptions);
  };

  toastErrorMissingPhoneNumber = (message: React.ReactNode) => {
    toast.error(message, this.toastErrorMissingPhoneNumberOptions);
  };

  toastAllert = (message: string | ToastContent | React.ReactNode) => {
    const contentToRender = React.isValidElement(message) ? (
      message
    ) : (
      <ToastMessage
        content={normalizeToToastContent(message)}
        testId={testIds.common.toast.warning}
      />
    );

    toast.error(contentToRender, this.toastAlertOptions);
  };

  toastOffline = () => {
    toast.error(
      <>
        You are offline. <br />
        Please check your internet connection
      </>,
      this.toastErrorOptions
    );
  };

  setIsOffline = (isOffline: boolean) => {
    this.isOffline = isOffline;
  };

  setIsGuestParcelCreation = (isGuestUser: boolean) => {
    this.isGuestParcelCreation = isGuestUser;
  };

  setLaunchNotificationShown = (value: boolean) => {
    this.isLaunchNotificationShown = value;
  };

  resetAppState = ({
    preserveParcelCreationState = false,
  }: ResetAppStateOptions = {}) => {
    if (!preserveParcelCreationState) {
      this.setIsGuestParcelCreation(false);
      store.navStore.resetNavStore();
      store.parcelCreationStore.resetAllData();
    } else {
      // We still want to close sidebars (e.g. so Map sidebar is reopened manually)
      store.navStore.closeAllRightSidebars();
    }
    this.setAccessToken(null);
    this.setRefreshToken(null);
    this.setGuestAccessToken(null);
    this.setGuestRefreshToken(null);
    store.userStore.user = null;
  };

  setAnalyticsInitialized = (value: boolean) => {
    this.isAnalyticsInitialized = value;
  };
}
